import React, { useState } from "react";
import { api } from "../../lib/api";
import { JOB_STATUS, INDUSTRY_NAMES } from "../../lib/industries";
import { Plus, Pencil, ExternalLink, Search, Users } from "lucide-react";
import JobFormModal from "./JobFormModal";
import ShareJob from "../ShareJob";
import { Badge } from "./Fields";
import { toast } from "sonner";

const FILTERS = [{ k: "", l: "All" }, { k: "active", l: "Active" }, { k: "on_hold", l: "On Hold" }, { k: "closed", l: "Closed" }];
const effStatus = (j) => (j.expired ? "closed" : j.status || (j.is_active ? "active" : "closed"));

export default function JobsManager({ jobs, reload }) {
  const [editing, setEditing] = useState(null);
  const [status, setStatus] = useState("");
  const [industry, setIndustry] = useState("");
  const [q, setQ] = useState("");

  const changeStatus = async (id, s) => {
    try { await api.patch(`/jobs/${id}/status`, { status: s }); toast.success(`Job marked ${JOB_STATUS[s].l}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const list = jobs.filter(j => (!status || effStatus(j) === status) && (!industry || j.industry === industry)
    && (!q || `${j.title} ${j.company_name} ${j.client_name || ""} ${j.location}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-4" data-testid="jobs-manager">
      <div className="ae-card p-4 flex flex-wrap gap-3 items-center">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {FILTERS.map(f => (
            <button key={f.k} onClick={() => setStatus(f.k)} data-testid={`jobs-filter-${f.k || "all"}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${status === f.k ? "bg-white text-[#0F2A4A] shadow-sm" : "text-slate-500 hover:text-[#0F2A4A]"}`}>
              {f.l} <span className="text-slate-400">({f.k ? jobs.filter(j => effStatus(j) === f.k).length : jobs.length})</span>
            </button>
          ))}
        </div>
        <select value={industry} onChange={e => setIndustry(e.target.value)} data-testid="jobs-filter-industry"
          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-[#C5A059]">
          <option value="">All industries</option>
          {INDUSTRY_NAMES.map(i => <option key={i}>{i}</option>)}
        </select>
        <div className="flex-1 min-w-[180px] flex items-center gap-2 px-3 rounded-xl border border-slate-200 bg-slate-50">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, client, location…"
            data-testid="jobs-manager-search" className="flex-1 py-2 bg-transparent outline-none text-sm" />
        </div>
        <button onClick={() => setEditing("new")} className="ae-btn-primary text-sm inline-flex items-center gap-2" data-testid="admin-post-job-btn">
          <Plus className="w-4 h-4" /> Post New Job
        </button>
      </div>

      {list.map(j => <JobRow key={j.id} j={j} onEdit={() => setEditing(j)} onStatus={changeStatus} />)}
      {list.length === 0 && <div className="ae-card p-10 text-center text-slate-500">No jobs match these filters.</div>}

      {editing && <JobFormModal job={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function JobRow({ j, onEdit, onStatus }) {
  const st = effStatus(j);
  return (
    <div className="ae-card p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-testid={`admin-job-${j.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-heading font-medium text-[#0F2A4A]">{j.title}</h4>
          <Badge cls={JOB_STATUS[st].cls} tid={`admin-job-status-${j.id}`}>{j.expired ? "Expired" : JOB_STATUS[st].l}</Badge>
          <Badge cls="bg-[#0F2A4A]/5 text-[#0F2A4A]">{j.industry}</Badge>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {j.company_name}{j.client_name ? ` · Client: ${j.client_name}` : ""} · {j.location}
          {j.vacancies ? ` · ${j.vacancies} vacancies` : ""}
        </div>
        <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><Users className="w-3 h-3" /> {j.applications_count || 0} applications</div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={j.expired ? "" : j.status} onChange={e => onStatus(j.id, e.target.value)} data-testid={`admin-job-status-select-${j.id}`}
          className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-[#C5A059]">
          {j.expired && <option value="" disabled>Expired — reactivate?</option>}
          <option value="active">Active</option><option value="on_hold">On Hold</option><option value="closed">Closed</option>
        </select>
        <button onClick={onEdit} data-testid={`admin-job-edit-${j.id}`}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[#C5A059] inline-flex items-center gap-1.5 transition-colors">
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <ShareJob job={j} testid={`admin-share-${j.id}`}
          triggerClassName="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 [&_svg]:w-3 [&_svg]:h-3" />
        <a href={`/jobs/${j.id}`} target="_blank" rel="noreferrer" data-testid={`admin-job-view-${j.id}`}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[#C5A059] inline-flex items-center gap-1.5 transition-colors">
          <ExternalLink className="w-3 h-3" /> View
        </a>
      </div>
    </div>
  );
}
